(() => {
  'use strict';

  // Shopify publishes the storefront root already carrying any locale or market
  // prefix, so on /en-ca/ this yields /en-ca/cart/add.js. A hardcoded "/cart..."
  // path reaches the primary market instead, which is how a localized storefront
  // ends up adding to the wrong cart and pricing in the wrong currency.
  const shopRoute = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;

  const Theme = {
    QuickView: {
      init() {
        this.modal = document.getElementById('QuickViewModal');
        this.content = document.getElementById('QuickViewContent');
        if (!this.modal || !this.content) return;

        this.closeBtn = this.modal.querySelector('.quick-view-close');
        this.strings = { loading: 'Loading', error: 'This product could not be loaded.', fullDetails: '' };
        try {
          Object.assign(this.strings, JSON.parse(this.modal.dataset.quickViewStrings || '{}'));
        } catch (err) {
          /* keep the defaults */
        }

        // Focus returns to whichever card opened the modal, not to the top of
        // the page, so keyboard and screen reader users keep their place in the
        // grid. <dialog> handles Escape and the backdrop click natively.
        this.opener = null;
        this.request = null;

        if (this.closeBtn) {
          this.closeBtn.addEventListener('click', () => this.modal.close());
        }

        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.modal.close();
        });

        this.modal.addEventListener('close', () => {
          if (this.request) {
            this.request.abort();
            this.request = null;
          }
          this.content.innerHTML = '';
          document.body.style.overflow = '';
          if (this.opener && document.contains(this.opener)) this.opener.focus();
          this.opener = null;
        });

        document.body.addEventListener('click', this.handleQuickViewClick.bind(this));
      },

      message(text, url) {
        const link = url
          ? `<p><a class="btn" href="${encodeURI(url)}">${this.strings.fullDetails}</a></p>`
          : '';
        return `<div class="quick-view-loading">${text}${link}</div>`;
      },

      async handleQuickViewClick(e) {
        // The floating icon on the card media, and the "Choose options" button
        // when it is set to open the quick view. The latter is an anchor to the
        // product page, so preventDefault below is what keeps us in the grid.
        const btn = e.target.closest('.quick-view-btn, [data-quick-view-trigger]');
        if (!btn) return;
        e.preventDefault();

        const handle = btn.dataset.productHandle;
        if (!handle) return;
        const productUrl = `${window.Shopify?.routes?.root || '/'}products/${handle}`.replace('//products', '/products');

        // A second click while one is still loading must not race the first.
        if (this.request) this.request.abort();
        this.request = new AbortController();

        this.opener = btn;
        this.content.innerHTML = this.message(this.strings.loading);
        if (!this.modal.open) this.modal.showModal();
        document.body.style.overflow = 'hidden';
        if (this.closeBtn) this.closeBtn.focus();

        try {
          // A section built for this modal, rather than a clone of the whole
          // product page with its duplicate ids and unrelated blocks.
          const res = await fetch(`${productUrl}?section_id=quick-view`, { signal: this.request.signal });
          if (!res.ok) throw new Error('Request failed');
          const text = await res.text();

          const doc = new DOMParser().parseFromString(text, 'text/html');
          const view = doc.querySelector('[data-quick-view]');
          if (!view) throw new Error('Section missing');

          this.content.innerHTML = view.outerHTML;
          // Lets injected parts that need a script (3D models) set themselves up.
          document.dispatchEvent(new CustomEvent('quick-view:loaded', { detail: { container: this.content } }));
          const focusTarget = this.content.querySelector('a, button, select, input:not([type="hidden"])');
          if (focusTarget) focusTarget.focus();
        } catch (err) {
          if (err.name === 'AbortError') return;
          this.content.innerHTML = this.message(this.strings.error, productUrl);
        } finally {
          this.request = null;
        }
      }
    },

    Carousel: {
      init() {
        const carousels = document.querySelectorAll('.card__carousel');
        carousels.forEach(c => {
          const wrapper = c.parentElement;
          const dots = wrapper.querySelectorAll('.card__carousel-dot');
          const prevBtn = wrapper.querySelector('.card__carousel-btn--prev');
          const nextBtn = wrapper.querySelector('.card__carousel-btn--next');
          
          if (!dots.length) return;
          
          c.addEventListener('scroll', () => {
            const scrollPct = c.scrollLeft / (c.scrollWidth - c.clientWidth);
            const index = Math.round(scrollPct * (dots.length - 1));
            dots.forEach((dot, i) => {
              dot.classList.toggle('is-active', i === index);
            });
          });

          if (prevBtn) {
            prevBtn.addEventListener('click', (e) => {
              e.preventDefault();
              c.scrollBy({ left: -c.clientWidth, behavior: 'smooth' });
            });
          }
          if (nextBtn) {
            nextBtn.addEventListener('click', (e) => {
              e.preventDefault();
              c.scrollBy({ left: c.clientWidth, behavior: 'smooth' });
            });
          }
        });
      }
    },

    Product: {
      init() {
        // Every media item in the gallery the trigger belongs to, in gallery order,
        // so the viewer pages through videos and models as well as photos.
        // Photos carry a URL; everything else reuses the embed markup the gallery
        // has already built, so the viewer needs no per-type knowledge.
        const galleryMedia = (source) => {
          const gallery = source?.closest?.('.product__gallery');
          if (!gallery) return [];
          // Thumbnails list every item in order. The stacked and masonry layouts
          // have no thumbnail strip, so their items stand in.
          let nodes = gallery.querySelectorAll('[data-thumb]');
          if (!nodes.length) {
            nodes = gallery.querySelectorAll('.product__stacked-media-item, .product__masonry-item');
          }
          const slides = [];
          nodes.forEach((node) => {
            const type = node.dataset.mediaType || 'image';
            const id = node.dataset.mediaId || '';
            if (type === 'image') {
              const img = node.querySelector('img');
              const url = node.dataset.fullImage || img?.dataset.full || img?.src;
              if (!url || slides.some((slide) => slide.url === url)) return;
              slides.push({ type, id, url, alt: img?.alt || '' });
              return;
            }
            // The panel for this item in the grid layouts, or the item itself in
            // the layouts that embed in place. Never the thumbnail button, whose
            // markup is a thumbnail image rather than a player.
            let embed = id ? gallery.querySelector(`[data-media-panel][data-media-id="${id}"]`) : null;
            if (!embed && node.matches('.product__stacked-media-item--embed, .product__masonry-item--embed')) {
              embed = node;
            }
            const html = embed?.innerHTML?.trim();
            if (!html) return;
            if (id && slides.some((slide) => slide.id === id)) return;
            slides.push({ type, id, html });
          });
          return slides;
        };

        const openZoom = (source) => {
          const slides = galleryMedia(source);
          if (!slides.length) return;

          // Open on whatever the gallery is currently showing: the media id of the
          // node clicked, else the highlighted thumbnail, else match the photo by
          // URL for a gallery that predates the media ids.
          const gallery = source?.closest?.('.product__gallery');
          const wantedId = source?.dataset?.mediaId
            || gallery?.querySelector('[data-thumb][aria-current="true"]')?.dataset.mediaId
            || '';
          let index = wantedId ? slides.findIndex((slide) => slide.id === wantedId) : -1;
          if (index === -1) {
            const img = source?.tagName === 'IMG' ? source : source?.querySelector?.('img');
            const url = source?.dataset?.fullImage || img?.dataset.full || img?.currentSrc || img?.src;
            if (url) index = slides.findIndex((slide) => slide.url === url);
          }
          if (index === -1) index = 0;

          // Labels come from the trigger so they stay translatable; this file is
          // a static asset and cannot use the `t` filter.
          let strings = { dialog: 'Product image viewer', close: 'Close', controls: 'Image zoom controls', in: 'Zoom in', out: 'Zoom out', reset: 'Reset zoom', previous: 'Previous image', next: 'Next image', fullscreen: 'Toggle full screen' };
          const trigger = source?.closest?.('.product__main-media')?.querySelector('[data-zoom-strings]');
          try { Object.assign(strings, JSON.parse(trigger?.dataset.zoomStrings || '{}')); } catch (error) { /* keep defaults */ }

          const icon = (paths) => `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;

          let viewer = document.getElementById('ProductImageViewer');
          if (!viewer) {
            viewer = document.createElement('dialog');
            viewer.id = 'ProductImageViewer';
            viewer.className = 'product-image-viewer';
            viewer.setAttribute('aria-label', strings.dialog);
            viewer.innerHTML = `
              <div class="product-image-viewer__panel">
                <p class="product-image-viewer__counter" data-viewer-counter aria-live="polite"></p>
                <div class="product-image-viewer__toolbar" role="group" aria-label="${strings.controls}">
                  <button type="button" data-image-zoom="out" aria-label="${strings.out}">${icon('<path d="M5 12h14"/>')}</button>
                  <button type="button" data-image-zoom="reset" aria-label="${strings.reset}"><span data-zoom-level>100%</span></button>
                  <button type="button" data-image-zoom="in" aria-label="${strings.in}">${icon('<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3M8 11h6M11 8v6"/>')}</button>
                  <button type="button" data-viewer-fullscreen aria-label="${strings.fullscreen}">${icon('<path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"/>')}</button>
                  <button type="button" class="product-image-viewer__close" aria-label="${strings.close}">
                    ${icon('<path d="M18 6 6 18M6 6l12 12"/>')}
                  </button>
                </div>
                <button type="button" class="product-image-viewer__nav product-image-viewer__nav--prev" data-viewer-prev aria-label="${strings.previous}">
                  ${icon('<path d="M15 18l-6-6 6-6"/>')}
                </button>
                <button type="button" class="product-image-viewer__nav product-image-viewer__nav--next" data-viewer-next aria-label="${strings.next}">
                  ${icon('<path d="M9 18l6-6-6-6"/>')}
                </button>
                <div class="product-image-viewer__stage">
                  <img alt="">
                  <div class="product-image-viewer__embed" data-viewer-embed hidden></div>
                </div>
              </div>`;
            document.body.appendChild(viewer);
            viewer.querySelector('.product-image-viewer__close').addEventListener('click', () => viewer.close());
            viewer.querySelector('[data-viewer-fullscreen]').addEventListener('click', () => {
              if (document.fullscreenElement) {
                document.exitFullscreen?.();
              } else {
                viewer.requestFullscreen?.();
              }
            });
            viewer.addEventListener('click', (event) => {
              if (event.target === viewer) viewer.close();
            });
          }

          const zoomImage = viewer.querySelector('img');
          const stage = viewer.querySelector('.product-image-viewer__stage');
          let embedBox = viewer.querySelector('[data-viewer-embed]');
          if (!embedBox) {
            embedBox = document.createElement('div');
            embedBox.className = 'product-image-viewer__embed';
            embedBox.dataset.viewerEmbed = '';
            embedBox.hidden = true;
            stage.appendChild(embedBox);
          }
          // Emptying the container unloads the iframe or video, which is also the
          // only way to stop a cross-origin player.
          const clearEmbed = () => { embedBox.innerHTML = ''; };
          let currentIsImage = true;
          let dragging = false;
          let startX = 0;
          let startY = 0;
          let panX = 0;
          let panY = 0;
          let startPanX = 0;
          let startPanY = 0;

          stage.onpointerdown = (event) => {
            if (!currentIsImage) return;
            if (parseFloat(zoomImage.dataset.zoom || '1') <= 1) return;
            dragging = true;
            startX = event.clientX;
            startY = event.clientY;
            startPanX = panX;
            startPanY = panY;
            stage.setPointerCapture(event.pointerId);
            stage.classList.add('is-dragging');
          };
          stage.onpointermove = (event) => {
            if (!dragging) return;
            panX = startPanX + event.clientX - startX;
            panY = startPanY + event.clientY - startY;
            const zoom = parseFloat(zoomImage.dataset.zoom || '1');
            zoomImage.style.transform = `translate(${panX}px, ${panY}px) scale(${zoom})`;
          };
          stage.onpointerup = (event) => {
            dragging = false;
            stage.releasePointerCapture?.(event.pointerId);
            stage.classList.remove('is-dragging');
          };
          stage.onpointercancel = stage.onpointerup;
          const reset = () => {
            panX = 0;
            panY = 0;
            zoomImage.style.transform = 'translate(0, 0) scale(1)';
            zoomImage.dataset.zoom = '1';
            viewer.querySelector('[data-zoom-level]').textContent = '100%';
          };
          // Paging resets the zoom, so a new image never opens mid-magnified
          // and off-centre from the previous one, and tears down the embed it is
          // leaving so a video does not keep playing out of sight.
          const show = (next) => {
            index = (next + slides.length) % slides.length;
            const slide = slides[index];
            currentIsImage = slide.type === 'image';
            clearEmbed();

            if (currentIsImage) {
              embedBox.hidden = true;
              zoomImage.hidden = false;
              zoomImage.src = slide.url;
              zoomImage.alt = slide.alt || '';
            } else {
              zoomImage.hidden = true;
              // removeAttribute, not src = '', which would refetch the page URL.
              zoomImage.removeAttribute('src');
              embedBox.hidden = false;
              embedBox.innerHTML = slide.html;
            }

            reset();
            // Magnification and dragging only mean something for a still image.
            stage.classList.toggle('is-embed', !currentIsImage);
            viewer.querySelectorAll('[data-image-zoom]').forEach((b) => { b.hidden = !currentIsImage; });

            const counter = viewer.querySelector('[data-viewer-counter]');
            if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
            const single = slides.length < 2;
            viewer.querySelectorAll('[data-viewer-prev], [data-viewer-next]').forEach((b) => { b.hidden = single; });
            if (counter) counter.hidden = single;
          };

          viewer.querySelector('[data-viewer-prev]').onclick = () => show(index - 1);
          viewer.querySelector('[data-viewer-next]').onclick = () => show(index + 1);
          viewer.onkeydown = (event) => {
            // An embedded player owns the arrow keys while it has focus.
            if (event.target.closest('[data-viewer-embed]')) return;
            if (event.key === 'ArrowRight') show(index + 1);
            if (event.key === 'ArrowLeft') show(index - 1);
          };
          viewer.onclose = clearEmbed;

          show(index);
          viewer.querySelectorAll('[data-image-zoom]').forEach((button) => {
            button.onclick = () => {
              const current = parseFloat(zoomImage.dataset.zoom || '1');
              const next = button.dataset.imageZoom === 'in' ? Math.min(current + .25, 4) : button.dataset.imageZoom === 'out' ? Math.max(current - .25, 1) : 1;
              zoomImage.dataset.zoom = String(next);
              if (next === 1) {
                panX = 0;
                panY = 0;
              }
              zoomImage.style.transform = `translate(${panX}px, ${panY}px) scale(${next})`;
              // Writing to the span keeps the reset button's aria-label intact.
              viewer.querySelector('[data-zoom-level]').textContent = `${Math.round(next * 100)}%`;
            };
          });
          viewer.showModal();
        };

        // "Image zoom" (section settings) travels on the gallery as data-image-zoom.
        // Galleries rendered before the setting existed have no attribute, so the
        // lightbox stays on by default and only an explicit mode turns it off.
        const lightboxEnabled = (el) => {
          const gallery = el && el.closest('[data-image-zoom]');
          if (!gallery) return true;
          return gallery.dataset.imageZoom === 'lightbox';
        };

        document.addEventListener('click', (e) => {
          const zoomButton = e.target.closest('.product__zoom');
          // Embedded players own their own clicks, so only the photo items open
          // the viewer; the video is reachable from there by paging.
          const media = e.target.closest(
            '.product__stacked-media-item:not(.product__stacked-media-item--embed), .product__masonry-item:not(.product__masonry-item--embed)'
          );
          const mainImage = e.target.closest('.product__main-media img');
          if (!zoomButton && !media && !mainImage) return;
          const source = zoomButton?.closest('.product__main-media') || media || mainImage;
          if (!lightboxEnabled(source)) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          openZoom(source);
        }, true);

        // Hover zoom: magnify the main image under the cursor. Pointer-based so
        // it stays off on touch, where there is no hover and the lightbox is the
        // better affordance.
        // The document itself is the target when the pointer leaves the window,
        // and only elements have closest().
        const elementFrom = (target) => (target instanceof Element ? target : null);

        const hoverZoomMove = (e) => {
          if (e.pointerType && e.pointerType !== 'mouse') return;
          const img = elementFrom(e.target)?.closest('.product__gallery--hover-zoom .product__main-media img');
          if (!img) return;
          const rect = img.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          img.style.transformOrigin = `${Math.min(100, Math.max(0, x))}% ${Math.min(100, Math.max(0, y))}%`;
          img.classList.add('is-hover-zoomed');
        };

        const hoverZoomClear = (e) => {
          const img = elementFrom(e.target)?.closest('.product__main-media img');
          if (!img) return;
          img.classList.remove('is-hover-zoomed');
          img.style.transformOrigin = '';
        };

        document.addEventListener('pointermove', hoverZoomMove, { passive: true });
        document.addEventListener('pointerleave', hoverZoomClear, true);
        document.addEventListener('pointerdown', hoverZoomClear, true);

        // Panel and lens zoom. Right side shows the magnified area in a panel
        // beside the image; magnific is a round lens that tracks the cursor
        // over the image itself, dimming the rest of the photo behind it.
        // Mouse only - there is no hover on touch, where the lightbox is the
        // right affordance.
        const ZOOM_LENS_SIZE = 340;
        const ZOOM_LENS_SCALE = 2.4;
        let zoomPopup = null;

        const popupGallery = (el) => {
          const gallery = el && el.closest('[data-image-zoom="right_side"], [data-image-zoom="magnific"]');
          return gallery || null;
        };

        const hidePopup = () => {
          if (!zoomPopup) return;
          zoomPopup.el.hidden = true;
          document.querySelectorAll('.product__main-media.is-lens-active').forEach((media) => {
            media.classList.remove('is-lens-active');
          });
        };

        const popupMove = (e) => {
          if (e.pointerType && e.pointerType !== 'mouse') return;
          const img = e.target.closest('.product__main-media img');
          const gallery = popupGallery(img);
          if (!img || !gallery) {
            hidePopup();
            return;
          }

          const rect = img.getBoundingClientRect();
          if (!rect.width || !rect.height) return;

          if (!zoomPopup) {
            const el = document.createElement('div');
            el.className = 'product-zoom-popup';
            el.setAttribute('aria-hidden', 'true');
            document.body.appendChild(el);
            zoomPopup = { el: el, src: '' };
          }

          const source = img.dataset.full || img.currentSrc || img.src;
          if (zoomPopup.src !== source) {
            zoomPopup.el.style.backgroundImage = `url("${source}")`;
            zoomPopup.src = source;
          }

          const media = img.closest('.product__main-media');
          const rightSide = gallery.dataset.imageZoom === 'right_side';
          zoomPopup.el.classList.toggle('product-zoom-popup--right', rightSide);
          zoomPopup.el.classList.toggle('product-zoom-popup--lens', !rightSide);

          if (rightSide) {
            if (media) media.classList.remove('is-lens-active');
            zoomPopup.el.style.top = `${rect.top}px`;
            zoomPopup.el.style.left = `${rect.right + 16}px`;
            zoomPopup.el.style.width = '';
            zoomPopup.el.style.height = `${rect.height}px`;
            zoomPopup.el.style.backgroundSize = '200%';
            const px = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
            const py = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
            zoomPopup.el.style.backgroundPosition = `${px}% ${py}%`;
          } else {
            // The lens centres on the cursor and shows the image scaled around
            // that exact point, so what is under the pointer is what magnifies.
            if (media) media.classList.add('is-lens-active');
            const half = ZOOM_LENS_SIZE / 2;
            const zoomedWidth = rect.width * ZOOM_LENS_SCALE;
            const zoomedHeight = rect.height * ZOOM_LENS_SCALE;
            const relX = e.clientX - rect.left;
            const relY = e.clientY - rect.top;
            let offsetX = relX * ZOOM_LENS_SCALE - half;
            let offsetY = relY * ZOOM_LENS_SCALE - half;
            // Clamped so the lens never reveals blank space past the edges.
            offsetX = Math.min(Math.max(offsetX, 0), Math.max(zoomedWidth - ZOOM_LENS_SIZE, 0));
            offsetY = Math.min(Math.max(offsetY, 0), Math.max(zoomedHeight - ZOOM_LENS_SIZE, 0));

            zoomPopup.el.style.width = `${ZOOM_LENS_SIZE}px`;
            zoomPopup.el.style.height = `${ZOOM_LENS_SIZE}px`;
            zoomPopup.el.style.left = `${e.clientX - half}px`;
            zoomPopup.el.style.top = `${e.clientY - half}px`;
            zoomPopup.el.style.backgroundSize = `${zoomedWidth}px ${zoomedHeight}px`;
            zoomPopup.el.style.backgroundPosition = `-${offsetX}px -${offsetY}px`;
          }

          zoomPopup.el.hidden = false;
        };

        document.addEventListener('pointermove', popupMove, { passive: true });
        document.addEventListener('pointerdown', hidePopup, true);
        window.addEventListener('scroll', hidePopup, { passive: true });

        // Quantity steppers
        document.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-qty-change]');
          if (!btn) return;
          const input = btn.closest('.qty')?.querySelector('input');
          if (!input) return;
          const step = parseInt(btn.dataset.qtyChange, 10);
          const min = parseInt(input.min || '1', 10);
          input.value = Math.max(min, (parseInt(input.value, 10) || min) + step);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // Swatch or dropdown selection updates hidden variant select.
        // Both picker types carry data-option-position, so one collector serves
        // the radio buttons and the "Dropdowns" picker alike.
        document.addEventListener('change', (e) => {
          const picker = e.target.closest('.swatch-input, .picker-select');
          if (!picker) return;

          const wrap = picker.closest('[data-product], .product');
          if (!wrap) return;

          const select = wrap.querySelector('[data-variant-select]');
          if (!select) return;

          // Collect selected options
          const chosen = Array.from(wrap.querySelectorAll('.swatch-input:checked, .picker-select'));
          const selectedValues = chosen.reduce((acc, input) => {
            acc[input.dataset.optionPosition] = input.value;
            return acc;
          }, {});

          // Find matching option in the hidden select
          const options = Array.from(select.options);
          const matchingOption = options.find(opt => {
            return (!selectedValues.option1 || opt.dataset.option1 === selectedValues.option1) &&
                   (!selectedValues.option2 || opt.dataset.option2 === selectedValues.option2) &&
                   (!selectedValues.option3 || opt.dataset.option3 === selectedValues.option3);
          });

          if (matchingOption) {
            matchingOption.selected = true;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        // Variant select updates price + availability
        document.addEventListener('change', (e) => {
          const select = e.target.closest('[data-variant-select]');
          if (!select) return;
          const opt = select.selectedOptions[0];
          const wrap = select.closest('[data-product], .product');
          if (!wrap || !opt) return;
          const priceEl = wrap.querySelector('[data-price]');
          const compareEl = wrap.querySelector('[data-compare-price]');
          // Scoped to the element: each variant <option> carries data-unit-price too.
          const unitEl = wrap.querySelector('.unit-price[data-unit-price]');
          const saleBadge = wrap.querySelector('[data-badge-sale]');
          const soldOutBadge = wrap.querySelector('[data-badge-soldout]');
          const button = wrap.querySelector('[data-add-button]');
          
          if (priceEl) priceEl.textContent = opt.dataset.price;
          if (compareEl) {
            const cmp = opt.dataset.compare || '';
            compareEl.textContent = cmp;
            compareEl.hidden = !cmp;
          }
          // Product page badges follow the variant: sold out replaces sale.
          const variantAvailable = opt.dataset.available === 'true';
          if (soldOutBadge) soldOutBadge.hidden = variantAvailable;
          if (saleBadge) {
            const saleText = (opt.dataset.saleBadge || '').trim();
            saleBadge.textContent = saleText;
            saleBadge.hidden = !saleText || !variantAvailable;
          }
          if (unitEl) {
            const unit = opt.dataset.unitPrice || '';
            const valueEl = unitEl.querySelector('[data-unit-price-value]');
            if (valueEl) valueEl.textContent = unit;
            unitEl.hidden = !unit;
          }
          if (button) {
            const available = opt.dataset.available === 'true';
            button.disabled = !available;
            button.textContent = available ? button.dataset.labelAdd : button.dataset.labelSoldout;
          }

          // Bring the gallery to the chosen variant's own photo. The matching
          // thumbnail is clicked rather than the image swapped here, so this
          // reuses the one switching path - active thumbnail, video panels,
          // zoom target - instead of keeping a second copy of that logic in
          // step with it. A variant with no photo of its own carries no media
          // id and leaves the gallery where the shopper put it.
          const mediaId = opt.dataset.mediaId;
          if (mediaId) {
            const thumb = wrap.querySelector(`[data-thumb][data-media-id="${mediaId}"]`);
            if (thumb && thumb.getAttribute('aria-current') !== 'true') thumb.click();
          }
        });

        // Stop whatever is playing in a panel we are about to hide. An iframe has
        // no pause API across origins, so its src is re-set, which is also how the
        // YouTube player is told to stop.
        const stopPanel = (panel) => {
          panel.querySelectorAll('video').forEach((v) => {
            try { v.pause(); } catch (_) {}
          });
          panel.querySelectorAll('iframe').forEach((frame) => {
            const src = frame.getAttribute('src');
            if (src) frame.setAttribute('src', src);
          });
        };

        // Thumbnail gallery. Photos share one swappable <img>; a video, external
        // video or 3D model has its own panel in the same box, so switching is a
        // matter of revealing the right one.
        document.addEventListener('click', (e) => {
          const thumb = e.target.closest('[data-thumb]');
          if (!thumb) return;
          const wrap = thumb.closest('[data-product], .product');
          const gallery = thumb.closest('.product__gallery') || wrap;
          const main = wrap?.querySelector('[data-main-image]');
          const img = thumb.querySelector('img');
          const mediaType = thumb.dataset.mediaType || 'image';
          const mediaId = thumb.dataset.mediaId;
          const panels = gallery ? gallery.querySelectorAll('[data-media-panel]') : [];

          if (mediaType === 'image') {
            const fullImage = thumb.dataset.fullImage || img?.dataset.full;
            if (main && img) {
              main.src = fullImage || img.src;
              main.srcset = '';
              main.alt = img.alt;
              main.hidden = false;
              // Keep the popup zoom pointing at the newly selected image.
              if (fullImage) main.dataset.full = fullImage;
            }
            panels.forEach((panel) => {
              if (!panel.hidden) stopPanel(panel);
              panel.hidden = true;
            });
          } else {
            panels.forEach((panel) => {
              const match = panel.dataset.mediaId === mediaId;
              if (!match && !panel.hidden) stopPanel(panel);
              panel.hidden = !match;
            });
            if (main) main.hidden = true;
          }

          wrap?.querySelectorAll('[data-thumb]').forEach((t) => t.setAttribute('aria-current', t === thumb ? 'true' : 'false'));
        });
      }
    },

    Cart: {
      init() {
        // Success confirmation by default; pass a message and isError for a failed
        // add. The message is set as text, never HTML, because it comes from the server.
        // Labels come translated from layout/theme.liquid; the English here is
        // only a fallback for a page that renders without the layout.
        const cartStrings = { adding: 'Adding...', added: 'Added to cart', tryAgain: 'Try again', error: "Couldn't add this item to your cart. Please try again." };
        try {
          // `t` HTML-escapes its output (an apostrophe arrives as &#39;), and these
          // strings are set as text, so decode the entities first.
          const decoder = document.createElement('textarea');
          const parsed = JSON.parse(document.querySelector('[data-cart-strings]')?.textContent || '{}');
          Object.keys(parsed).forEach((key) => {
            decoder.innerHTML = parsed[key];
            cartStrings[key] = decoder.value;
          });
        } catch (err) {
          /* keep the defaults */
        }
        const showCartToast = (message = cartStrings.added, isError = false) => {
          let toast = document.querySelector('[data-cart-toast]');
          if (!toast) {
            toast = document.createElement('div');
            toast.dataset.cartToast = '';
            toast.className = 'cart-toast';
            document.body.appendChild(toast);
          }
          toast.setAttribute('role', isError ? 'alert' : 'status');
          toast.setAttribute('aria-live', isError ? 'assertive' : 'polite');
          toast.classList.toggle('cart-toast--error', isError);
          const icon = document.createElement('span');
          icon.className = 'cart-toast__icon';
          icon.setAttribute('aria-hidden', 'true');
          icon.textContent = isError ? '!' : '✓';
          const text = document.createElement('span');
          text.textContent = message;
          toast.replaceChildren(icon, text);
          toast.classList.remove('is-visible');
          void toast.offsetWidth;
          toast.classList.add('is-visible');
          clearTimeout(toast._hideTimer);
          toast._hideTimer = setTimeout(() => toast.classList.remove('is-visible'), isError ? 5000 : 2800);
        };

        // /cart/add.js answers a refused add (sold out, maximum quantity already in
        // the cart, …) with a 422 whose description is already in the shop's language.
        const cartAddError = async (response) => {
          const data = await response.json().catch(() => ({}));
          return new Error(data.description || data.message || '');
        };
        const CART_ADD_FALLBACK = cartStrings.error;
        // Flies a copy of the product image into the cart icon so an add reads as
        // one continuous motion instead of a silent state change. Opt-out lives in
        // Theme settings > Cart, and reduced-motion visitors always skip it.
        const cartTarget = () =>
          document.getElementById('mkt-cart-btn') ||
          document.querySelector('[data-cart-count]')?.closest('a, button') ||
          document.querySelector('[data-cart-count]');

        // The product photo, or the placeholder drawing a card shows when the
        // product has no image, so the flight still has something to carry.
        const productImageFor = (form) => {
          const scope = form.closest('[data-product], .product, .card, .product-card, .featured-product');
          return scope?.querySelector('[data-main-image], img, .placeholder-svg') || document.querySelector('[data-main-image]');
        };

        const flyToCart = (form) => {
          if (document.body.dataset.cartAddAnimation === 'none') return;
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
          if (typeof Element.prototype.animate !== 'function') return;

          const target = cartTarget();
          const source = productImageFor(form);
          if (!target || !source) return;

          const from = source.getBoundingClientRect();
          const to = target.getBoundingClientRect();
          if (!from.width || !from.height || !to.width) return;

          let ghost;
          if (source.tagName === 'IMG') {
            const src = source.currentSrc || source.src;
            if (!src) return;
            ghost = document.createElement('img');
            ghost.src = src;
            ghost.alt = '';
          } else {
            ghost = source.cloneNode(true);
            ghost.removeAttribute('id');
            ghost.style.background = getComputedStyle(source.parentElement).backgroundColor;
          }
          ghost.setAttribute('aria-hidden', 'true');
          Object.assign(ghost.style, {
            position: 'fixed',
            left: `${from.left}px`,
            top: `${from.top}px`,
            width: `${from.width}px`,
            height: `${from.height}px`,
            objectFit: 'cover',
            borderRadius: '12px',
            zIndex: '9999',
            pointerEvents: 'none',
            willChange: 'transform, opacity'
          });
          document.body.appendChild(ghost);

          const dx = to.left + to.width / 2 - (from.left + from.width / 2);
          const dy = to.top + to.height / 2 - (from.top + from.height / 2);

          const flight = ghost.animate(
            [
              { transform: 'translate(0, 0) scale(1)', opacity: 1, borderRadius: '12px' },
              { transform: `translate(${dx * 0.55}px, ${dy * 0.5 - 70}px) scale(0.55)`, opacity: 0.95, offset: 0.55 },
              { transform: `translate(${dx}px, ${dy}px) scale(0.1)`, opacity: 0.15, borderRadius: '50%' }
            ],
            { duration: 720, easing: 'cubic-bezier(0.36, 0, 0.2, 1)' }
          );

          const land = () => {
            ghost.remove();
            // The cart lid "catches" the item as the flight lands.
            target.classList.remove('is-catching');
            void target.offsetWidth;
            target.classList.add('is-catching');
            setTimeout(() => target.classList.remove('is-catching'), 420);
          };
          flight.onfinish = land;
          flight.oncancel = () => ghost.remove();
        };

        const refreshHeaderCount = (cart) => {
          document.querySelectorAll('[data-cart-count], [data-cart-drawer-count]').forEach((el) => {
            el.textContent = String(cart.item_count);
            el.dataset.count = String(cart.item_count);
            el.classList.remove('is-bumping');
            void el.offsetWidth;
            el.classList.add('is-bumping');
          });
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: cart.item_count } }));
        };

        // Shopify's dynamic checkout button posts through its own script instead
        // of submitting the form, so native constraint validation never runs and
        // a required gate such as the terms checkbox is silently skipped. Check
        // it here, in the capture phase, before that script sees the click.
        document.addEventListener('click', (e) => {
          const payButton = e.target.closest(
            '.shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, shopify-buy-it-now-button'
          );
          if (!payButton) return;
          const form = payButton.closest('form');
          if (!form || form.checkValidity()) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          form.reportValidity();
        }, true);

        // The accelerated buttons sit in iframes, where the listener above
        // cannot reach them, so the whole block is made inert until the terms
        // box is ticked. It is rendered enabled, so if this never runs checkout
        // still works rather than being permanently blocked.
        const syncTermsGate = (form) => {
          const gate = form.querySelector('[data-terms-gate]');
          if (!gate) return;
          const agreed = Array.from(form.querySelectorAll('input[type="checkbox"][required]')).every((box) => box.checked);
          gate.inert = !agreed;
          if (agreed) {
            gate.removeAttribute('data-terms-blocked');
          } else {
            gate.setAttribute('data-terms-blocked', '');
          }
        };

        // Queried from the gate up, not with form:has(...): an unsupported
        // :has() throws a SyntaxError and would take the rest of this block
        // down with it.
        document.querySelectorAll('[data-terms-gate]').forEach((gate) => {
          const form = gate.closest('form');
          if (form) syncTermsGate(form);
        });

        document.addEventListener('change', (e) => {
          const box = e.target.closest('input[type="checkbox"][required]');
          if (!box) return;
          const form = box.closest('form');
          if (form) syncTermsGate(form);
        });

        // Product detail add-to-cart stays on the current page.
        document.addEventListener('submit', async (e) => {
          const form = e.target.closest('form[data-ajax-add]');
          if (!form) return;
          e.preventDefault();
          const button = form.querySelector('[data-add-button]');
          if (!button || button.disabled || button.classList.contains('is-adding')) return;
          const label = button.querySelector('span') || button;
          const original = label.textContent;
          button.disabled = true;
          button.classList.add('is-adding');
          label.textContent = cartStrings.adding;
          try {
            const response = await fetch(shopRoute('cart/add.js'), { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: new FormData(form) });
            if (!response.ok) throw await cartAddError(response);
            const cart = await (await fetch(shopRoute('cart.js'))).json();
            refreshHeaderCount(cart);
            button.classList.remove('is-adding');
            button.classList.add('is-added');
            label.textContent = cartStrings.added;
            flyToCart(form);
            showCartToast();
            const drawer = document.getElementById('mkt-cart-drawer');
            if (drawer?.dataset.openOnAdd !== 'false') document.getElementById('mkt-cart-btn')?.click();
            setTimeout(() => { button.classList.remove('is-added'); label.textContent = original; button.disabled = false; }, 1200);
          } catch (error) {
            showCartToast(error.message || CART_ADD_FALLBACK, true);
            button.classList.remove('is-adding'); button.classList.add('is-add-error'); label.textContent = cartStrings.tryAgain;
            setTimeout(() => { button.classList.remove('is-add-error'); label.textContent = original; button.disabled = false; }, 1600);
          }
        });

        // Cart-page remove updates the server and header count without following the remove URL.
        document.addEventListener('click', async (e) => {
          const remove = e.target.closest('[data-cart-remove-key]');
          if (!remove) return;
          e.preventDefault();
          remove.classList.add('is-removing');
          try {
            const response = await fetch(shopRoute('cart/change.js'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: remove.dataset.cartRemoveKey, quantity: 0 }) });
            if (!response.ok) throw new Error('Remove failed');
            const cart = await response.json();
            refreshHeaderCount(cart);
            window.location.reload();
          } catch (_) { remove.classList.remove('is-removing'); }
        });

        // Quick add stays on the page and feeds the animated cart drawer.
        document.addEventListener('submit', async (e) => {
          const form = e.target.closest('.card__quick-add-form');
          if (!form) return;
          e.preventDefault();

          const button = form.querySelector('button[type="submit"]');
          if (!button || button.disabled) return;
          const label = button.querySelector('span') || button;
          const originalLabel = label.textContent;
          button.disabled = true;
          button.classList.add('is-adding');
          label.textContent = cartStrings.adding;

          try {
            const response = await fetch(form.action, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ id: form.querySelector('[name="id"]').value, quantity: 1 })
            });
            if (!response.ok) throw await cartAddError(response);
            const cart = await (await fetch(shopRoute('cart.js'))).json();
            refreshHeaderCount(cart);
            button.classList.remove('is-adding');
            button.classList.add('is-added');
            label.textContent = cartStrings.added;
            flyToCart(form);
            showCartToast();
            const drawer = document.getElementById('mkt-cart-drawer');
            if (drawer?.dataset.openOnAdd !== 'false') document.getElementById('mkt-cart-btn')?.click();
            window.setTimeout(() => {
              button.classList.remove('is-added');
              label.textContent = originalLabel;
              button.disabled = false;
            }, 1200);
          } catch (error) {
            showCartToast(error.message || CART_ADD_FALLBACK, true);
            button.classList.remove('is-adding');
            button.classList.add('is-add-error');
            label.textContent = cartStrings.tryAgain;
            window.setTimeout(() => {
              button.classList.remove('is-add-error');
              label.textContent = originalLabel;
              button.disabled = false;
            }, 1600);
          }
        });

        document.addEventListener('cart:refresh', async () => {
          try {
            const cart = await (await fetch(shopRoute('cart.js'))).json();
            refreshHeaderCount(cart);
            showCartToast();
          } catch (_) {}
        });

        // Auto-submit on quantity change
        document.addEventListener('change', (e) => {
          const input = e.target.closest('[data-cart-qty]');
          if (input) input.closest('form')?.submit();
        });
      }
    },

    Collection: {
      init() {
        // Sort select navigates
        document.addEventListener('change', (e) => {
          const sort = e.target.closest('[data-sort-select]');
          if (!sort) return;
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', sort.value);
          url.searchParams.delete('page');
          window.location.assign(url.toString());
        });
      }
    },

    Reveal: {
      init() {
        this.style = document.body.dataset.sectionReveal || 'none';
        if (this.style === 'none' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        // The theme editor's "Add section" previews render in a frame that is
        // never scrolled into view, so a hidden section would stay blank there
        // and show as "No preview available". Previews skip the reveal.
        if (window.Shopify && window.Shopify.visualPreviewMode) return;
        this.once = document.body.dataset.sectionRevealOnce === 'true';
        this.skipFirst = document.body.dataset.sectionRevealSkipFirst === 'true';
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              if (this.once) this.observer.unobserve(entry.target);
            } else if (!this.once) {
              entry.target.classList.remove('is-visible');
            }
          });
        // Trigger on any overlap past the bottom 8% of the viewport, not on a
        // ratio of the section: a ratio threshold can never be met by a section
        // taller than viewport / ratio (e.g. a 48-product grid on mobile), which
        // would leave it at opacity 0 for good.
        }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
        this.observeAll();
        document.body.classList.add('section-reveal-ready');
        document.addEventListener('shopify:section:load', (event) => this.observe(event.target));
      },

      observeAll() {
        const sections = [...document.querySelectorAll('.shopify-section')];
        const firstContentSection = document.querySelector('main .shopify-section');
        sections.forEach((section, index) => {
          if (this.skipFirst && section === firstContentSection) {
            section.classList.add('is-visible');
            return;
          }
          this.observe(section, index);
        });
      },

      observe(section, index = 0) {
        if (!section || section.classList.contains('theme-reveal')) return;
        if (section.querySelector('[data-animation]:not([data-animation="none"])')) {
          section.classList.add('is-visible');
          return;
        }
        const stagger = Number.parseInt(getComputedStyle(document.body).getPropertyValue('--section-reveal-stagger'), 10) || 0;
        section.style.setProperty('--section-reveal-delay', `${(index % 4) * stagger}ms`);
        section.classList.add('theme-reveal');
        this.observer.observe(section);
      }
    },

    init() {
      this.QuickView.init();
      this.Carousel.init();
      this.Product.init();
      this.Cart.init();
      this.Collection.init();
      this.Reveal.init();
    }
  };

  document.addEventListener('DOMContentLoaded', () => Theme.init());

})();
